import React, { ReactElement, useState } from 'react'
import LaunchIcon from '@mui/icons-material/Launch'
import { Avatar, Box, Chip, Link, Typography } from '@mui/material'

import { RelatedContactEntry2 } from '~types'

interface RelatedContactChipProps {
  contact: RelatedContactEntry2
  onDelete?: () => void
}

const RelatedContactChip = ({ contact, onDelete }: RelatedContactChipProps): ReactElement => {
  const [expanded, setExpanded] = useState(false)

  return (
    <Box
      sx={{ position: 'relative' }}
      onClick={(e) => e.stopPropagation()}
      onMouseLeave={() => setExpanded(false)}
    >
      <Chip
        avatar={<Avatar alt={contact.name} src={contact.image || undefined} />}
        label={contact.relation === '' ? 'Related Contact' : `${contact.relation}: ${contact.name}`}
        onMouseEnter={() => setExpanded(true)}
        sx={{
          '& .MuiChip-label': {
            paddingLeft: '8px',
          },
          '& .MuiChip-avatar': {
            marginLeft: '6px',
            marginRight: '0px',
          },
          mb: 1,
          mr: 1,
        }}
        onDelete={onDelete}
      />
      <Box
        sx={{
          paddingTop: '8px',
          opacity: expanded ? 1 : 0,
          visibility: expanded ? 'visible' : 'hidden',
          transition: 'opacity 0.2s ease-in-out, visibility 0.2s ease-in-out',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            zIndex: 1000,
            backgroundColor: 'background.paper',
            padding: '16px',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '4px',
            boxShadow: 2,
            minWidth: '250px',
          }}
        >
          <Link
            href={`/organizations/${contact.owner}/contacts/${contact.handle}`}
            target='_blank'
            rel='noopener noreferrer'
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              mb: 1,
              color: 'primary.main',
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
          >
            Go to Contact Page
            <LaunchIcon sx={{ fontSize: 16 }} />
          </Link>

          {/* Rest of the content */}
          {contact.emails.length > 0 &&
            contact.emails.map((email) => (
              <Typography key={email.id} variant='body2'>
                <Box component='span' sx={{ fontWeight: 600 }}>
                  Email:
                </Box>{' '}
                {email.address}
              </Typography>
            ))}

          {contact.phones.length > 0 &&
            contact.phones.map((phone) => (
              <Typography key={phone.phone_number_id} variant='body2'>
                <Box component='span' sx={{ fontWeight: 600 }}>
                  Phone:
                </Box>{' '}
                {phone.phone_number}
              </Typography>
            ))}

          {contact.addresses.length > 0 &&
            contact.addresses.map((address) => (
              <Typography key={address.id} variant='body2'>
                <Box component='span' sx={{ fontWeight: 600 }}>
                  Address:
                </Box>{' '}
                {address.street}, {address.city}, {address.state} {address.zip}
              </Typography>
            ))}

          {contact.tags.length > 0 && (
            <Typography variant='body2'>
              <Box component='span' sx={{ fontWeight: 600 }}>
                Tags:
              </Box>{' '}
              {contact.tags.map((tag) => tag.name).join(', ')}
            </Typography>
          )}

          {contact.attributions.length > 0 &&
            contact.attributions.map((attr) => (
              <Typography key={attr.id} variant='body2'>
                <Box component='span' sx={{ fontWeight: 600 }}>
                  {attr.attribute.name}:
                </Box>{' '}
                {attr.value}
              </Typography>
            ))}
        </Box>
      </Box>
    </Box>
  )
}

export default RelatedContactChip
